import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "../../lib/utils"

// Task #7901 — dialogs scale out from the control that summoned them instead
// of always zooming from viewport centre (matching how Radix popovers and
// dropdowns anchor to their trigger). We can't ask Radix for the trigger's
// rect from here, so a lightweight capture-phase listener remembers the last
// pointerdown; when DialogContent mounts shortly after a pointer interaction,
// its transform-origin is anchored to that point (clamped inside the panel).
// Keyboard-opened dialogs (no recent pointerdown) keep the centred default,
// so this is strictly additive and safe for every consumer (incl. the admin
// console).
type PointerSnapshot = { x: number; y: number; t: number }
let lastPointerDown: PointerSnapshot | null = null
if (typeof document !== "undefined") {
  document.addEventListener(
    "pointerdown",
    (e) => {
      lastPointerDown = { x: e.clientX, y: e.clientY, t: Date.now() }
    },
    { capture: true, passive: true }
  )
}

/** How long after a pointerdown a mounting dialog still counts as "opened by it". */
const POINTER_ORIGIN_WINDOW_MS = 1500

// Task #7901 — shared overlay scrim material: a dimmed, lightly blurred
// backdrop instead of a flat 80% blackout. Progressive enhancement: the
// blurred material is the default wherever backdrop-filter is supported;
// browsers without it get a solid darker dim. Users who positively request
// reduced transparency or more contrast get the solid dim back via
// important-flagged overrides (positive `reduce`/`more` queries only —
// gating on `no-preference` would disable the effect in browsers that don't
// implement the media feature at all, e.g. Chromium for
// prefers-reduced-transparency).
const overlayScrimClass =
  "bg-black/70 supports-[backdrop-filter]:bg-black/50 supports-[backdrop-filter]:backdrop-blur-[2px] [@media(prefers-reduced-transparency:reduce)]:bg-black/70! [@media(prefers-reduced-transparency:reduce)]:backdrop-blur-none! [@media(prefers-contrast:more)]:bg-black/70! [@media(prefers-contrast:more)]:backdrop-blur-none!"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      overlayScrimClass,
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const localRef = React.useRef<React.ElementRef<typeof DialogPrimitive.Content> | null>(null)
  const setRefs = React.useCallback(
    (node: React.ElementRef<typeof DialogPrimitive.Content> | null) => {
      localRef.current = node
      if (typeof ref === "function") ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  // Anchor the zoom's transform-origin to the pointer position that opened
  // the dialog (see note above). Runs once per mount; the inline style
  // persists through the exit animation so close mirrors open.
  React.useLayoutEffect(() => {
    const node = localRef.current
    const p = lastPointerDown
    if (!node || !p || Date.now() - p.t > POINTER_ORIGIN_WINDOW_MS) return
    const rect = node.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    const x = Math.min(Math.max(p.x - rect.left, 0), rect.width)
    const y = Math.min(Math.max(p.y - rect.top, 0), rect.height)
    node.style.transformOrigin = `${x}px ${y}px`
  }, [])

  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
